import CircularProgress from '@mui/material/CircularProgress';
import { useBooleanState, Iconify } from '@mfw/components';
import { useQueryClient } from '@tanstack/react-query';
import IconButton from '@mui/material/IconButton';
import { OrgBillingStatus, SubscriptionStatus } from '@mfw/common';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import React from 'react';

import type { Routes } from '../../router';

import { useRefreshOrganizationBilling } from '../../hooks/platformBilling/useRefreshOrganizationBilling';
import { buildAccountBillingRoute } from '../AccountBilling';
import {
  useAuthContext,
  useOrganizationContext,
  usePermissionsContext,
  useSnackbar,
} from '../../context';
import { useOrgBillingOverview, usePayNow } from '../../hooks/platformBilling';
import { resolveBillingBannerState } from './billingBannerState';
import { usePollUntil } from '../../hooks/usePollUntil';
import { HttpStatus } from '../../api/types/api.types';
import { useNavigate } from '../../router/hooks';
import { queryKeys } from '../../queryKeys';
import messages from './messages';

const DATE_FORMAT = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
} as const;

function trialDismissKey(organizationId: string) {
  return `billing-banner-trial-dismissed:${organizationId}`;
}

/**
 * Global trial/dunning banner (F2.4, owner decision: GLOBAL in AppLayout,
 * conditional render). The overview QUERY still runs only with org context +
 * `billing_read` — a client/trainer without the permission never makes the
 * request. States: TRIALING → info (dismissible per session); PAST_DUE/GRACE
 * → warning + "Pay now" + CTA to cards; SUSPENDED → blocking alert with "Pay
 * now" as the primary action (the only self-service way out — audit #2).
 * Billing is asynchronous → after pay-now the overview is polled until
 * ACTIVE, or an ENDED plan with no live subscription after final settlement.
 *
 * §16 (R-AUD-62): which state applies is `resolveBillingBannerState`, not a
 * subscription status read here — see that file for why the plan decides the
 * block. A member WITHOUT `billing_read` renders too, from
 * `organization.billingLocked` alone: they cannot read the overview and cannot
 * settle the debt, but they were the ones left with a silently closed
 * organization and no explanation anywhere, so they get the sentence without
 * the actions.
 */
export const BillingStatusBanner = () => {
  const { formatDate, formatMessage } = useIntl();
  const { currentOrganization } = useOrganizationContext();
  const { currentUser } = useAuthContext();
  const { hasPermission } = usePermissionsContext();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const refreshOrganizationBilling = useRefreshOrganizationBilling();
  const navigate = useNavigate();

  const organizationId = currentOrganization?.id ?? '';
  const canReadBilling = hasPermission('billing_read');
  const enabled = !!organizationId && canReadBilling;
  // §16 — readable without `billing_read`, so a client or trainer of a locked
  // organization finally gets told why the organization went quiet.
  const organizationLocked =
    !!organizationId && Boolean(currentOrganization?.billingLocked);
  // Q3/Task 4 — and WHICH lock it is. Same row, same chokepoint, same
  // readability: without it every member of an organization that cancelled and
  // owes nothing read "settle the debt to restore access".
  const organizationLockReason = currentOrganization?.billingLockReason;

  const trialDismissed = useBooleanState();
  const payNowPolling = useBooleanState();
  // The organization switcher stays available while a charge settles. Keep
  // the asynchronous flow attached to the organization that started it;
  // `organizationId` below is live context and may change before the poll ends.
  const payNowOrganizationIdRef = React.useRef<string>();

  React.useEffect(() => {
    if (!organizationId) return;
    trialDismissed.setValue(
      sessionStorage.getItem(trialDismissKey(organizationId)) === '1',
    );
  }, [organizationId, trialDismissed]);

  const { data: overview } = useOrgBillingOverview({
    enabled,
    organizationId,
  });
  const { isLoading: isPayingNow, mutate: payNowMutate } = usePayNow();

  const { status: payNowPollStatus } = usePollUntil({
    enabled: payNowPolling.isActive,
    isDone: (fresh) =>
      fresh?.subscription?.status === SubscriptionStatus.ACTIVE ||
      (fresh?.subscription === null &&
        fresh.plan?.status === OrgBillingStatus.ENDED),
    poll: () => {
      const payNowOrganizationId =
        payNowOrganizationIdRef.current ?? organizationId;
      const { queryFn, queryKey } =
        queryKeys.platformBilling.fetchOrgBillingOverview({
          organizationId: payNowOrganizationId,
        });
      return queryClient.fetchQuery({ queryFn, queryKey, staleTime: 0 });
    },
  });

  React.useEffect(() => {
    if (payNowPollStatus !== 'done' && payNowPollStatus !== 'timeout') return;
    const payNowOrganizationId =
      payNowOrganizationIdRef.current ?? organizationId;
    payNowPolling.off();
    enqueueSnackbar({
      message: formatMessage(
        payNowPollStatus === 'done'
          ? messages.bannerPayNowSuccess
          : messages.bannerPayNowFailed,
      ),
      variant: payNowPollStatus === 'done' ? 'success' : 'warning',
    });
    // Completion may restore access or end a cancelling subscription. Refresh
    // both the guard's row and the provider snapshot so the lock AND its reason
    // follow the settled plan. Pay-now's API response only schedules a charge;
    // a timeout does not prove either success or failure.
    if (payNowPollStatus === 'done') {
      refreshOrganizationBilling(payNowOrganizationId).catch(() => undefined);
    }
    payNowOrganizationIdRef.current = undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payNowPollStatus]);

  if (!enabled && !organizationLocked) return null;

  const subscription = overview?.subscription ?? null;
  const state = resolveBillingBannerState(overview, {
    organizationLocked,
    organizationLockReason,
  });
  if (state === null) return null;

  const handlePayNow = () => {
    payNowOrganizationIdRef.current = organizationId;
    payNowMutate(
      { organizationId },
      {
        onError: (error) => {
          // 409 = BillingRunner already holds the subscription lock → just continue polling.
          if (error?.status === HttpStatus.Conflict) {
            payNowPolling.on();
            return;
          }
          payNowOrganizationIdRef.current = undefined;
        },
        onSuccess: () => payNowPolling.on(),
      },
    );
  };

  // T19 — the CTA used to land on the legacy `FinancePage`'s Cards tab, which
  // now just redirects here anyway for a regular user. It keeps landing on a
  // CARD list (`tab: 'cards'`) because that is what it offers to do: the
  // pay-now context is the sibling button next to it, and `tab: 'overview'`
  // has no cards on it at all. `OrgBillingLockedScreen`'s single CTA stays on
  // `overview` for the same reason — it is a pay-now CTA, not an add-card one.
  const goToCards = () => {
    navigate(
      buildAccountBillingRoute({
        userId: currentUser?.id ?? '',
        scope: organizationId,
        tab: 'cards',
      }) as Routes,
    );
  };

  // Q3/Task 4 — the ended lock's only action. Nothing is owed, so a card list
  // and a pay-now button both answer the wrong question; the package catalog
  // lives on the `subscriptions` tab.
  const goToPlans = () => {
    navigate(
      buildAccountBillingRoute({
        userId: currentUser?.id ?? '',
        scope: organizationId,
        tab: 'subscriptions',
      }) as Routes,
    );
  };

  // F4 (platform-user-class, P2 §6b-2): a platform actor never triggers a
  // charge — the client pays for themselves. The banner stays informative;
  // its pay-now / add-card actions disappear (BE routes 403 them anyway).
  // §16: same for a member without `billing_read` — the banner now reaches them
  // so they learn WHY the organization is closed, but settling the debt is not
  // theirs to do and both routes behind these buttons would 403.
  const payNowActions =
    currentUser?.isPlatformUser || !canReadBilling ? null : (
      <Box alignItems="center" display="flex" gap={1}>
        {payNowPolling.isActive ? (
          <Box alignItems="center" display="flex" gap={1} pr={1}>
            <CircularProgress color="inherit" size={16} />
            {formatMessage(messages.bannerPayNowProcessing)}
          </Box>
        ) : (
          <Button
            data-testid="billing-banner-pay-now"
            onClick={handlePayNow}
            disabled={isPayingNow}
            color="inherit"
            size="small"
          >
            {formatMessage(messages.bannerPayNow)}
          </Button>
        )}
        <Button
          data-testid="billing-banner-go-to-cards"
          onClick={goToCards}
          color="inherit"
          size="small"
        >
          {formatMessage(messages.bannerGoToCards)}
        </Button>
      </Box>
    );

  // Q3/Task 4: the organization is restricted because it has no plan any more,
  // NOT because it owes anything. Warning rather than error — nothing here is
  // failing — and no payment action, because there is nothing to pay.
  if (state === 'subscriptionEnded') {
    return (
      <Alert
        action={
          currentUser?.isPlatformUser || !canReadBilling ? null : (
            <Button
              data-testid="billing-banner-choose-plan"
              onClick={goToPlans}
              color="inherit"
              size="small"
            >
              {formatMessage(messages.bannerChoosePlan)}
            </Button>
          )
        }
        data-testid="billing-banner-subscription-ended"
        severity="warning"
        square
      >
        {formatMessage(messages.bannerSubscriptionEndedAlert)}
      </Alert>
    );
  }

  if (state === 'trial') {
    if (trialDismissed.isActive) return null;

    return (
      <Alert
        action={
          <IconButton
            onClick={() => {
              sessionStorage.setItem(trialDismissKey(organizationId), '1');
              trialDismissed.on();
            }}
            data-testid="billing-banner-trial-dismiss"
            color="inherit"
            size="small"
          >
            <Iconify icon="mdi:close" size="small" />
          </IconButton>
        }
        data-testid="billing-banner-trial"
        severity="info"
        square
      >
        {formatMessage(messages.bannerTrialInfo, {
          date: subscription?.trialEndsAt
            ? formatDate(subscription.trialEndsAt, DATE_FORMAT)
            : '—',
        })}
      </Alert>
    );
  }

  if (state === 'pastDue') {
    return (
      <Alert
        data-testid="billing-banner-past-due"
        action={payNowActions}
        severity="warning"
        square
      >
        {subscription?.graceUntil
          ? formatMessage(messages.bannerPastDueWarning, {
              date: formatDate(subscription.graceUntil, DATE_FORMAT),
            })
          : formatMessage(messages.bannerPastDueWarningNoDate)}
      </Alert>
    );
  }

  if (state === 'suspended') {
    return (
      <Alert
        data-testid="billing-banner-suspended"
        action={payNowActions}
        severity="error"
        square
      >
        {formatMessage(messages.bannerSuspendedAlert)}
      </Alert>
    );
  }

  return null;
};
